import React from "react";

// Component imports
import { SearchIcon } from "@icons";
import { TextField, InputAdornment } from "@material";

// Misc Imports

import { useTranslation } from "@i18n";
import { makeStyles } from "@styles";

const useStyles = (styleProps) =>
  makeStyles((theme: Theme) => ({
    searchBar: {
      width: styleProps.width || "100%",
      maxWidth: styleProps.maxWidth || "100%",
    },
    root: {
      backgroundColor: theme.palette.colors.background.lightGrey,
      minHeight: 44,
      maxHeight: 44,
      padding: 8,
      borderRadius: 8,
      border: `1px solid transparent`,
      transitionProperty: "border-color, background-color",
      transitionDuration: "0.3s",
      "&:hover": {
        borderColor: theme.palette.colors.border.light,
      },
      "&.Mui-focused": {
        borderColor: theme.palette.colors.border.light,
        backgroundColor: theme.palette.white,
      },
      "&&&:before": {
        borderBottom: "none",
      },
      "&&:after": {
        borderBottom: "none",
      },
      "&&&&:hover:before": {
        borderBottom: "none",
      },
    },
    input: {
      fontSize: 15,
      fontWeight: 400,
      color: theme.palette.black,
      paddingRight: 2,
      "&::placeholder": {
        color: theme.palette.colors.text.subheader,
        opacity: 1,
      },
    },
    clearButton: {
      width: 28,
      height: 28,
      border: 0,
      borderRadius: 14,
      backgroundColor: "transparent",
      color: theme.palette.colors.text.subheader,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 22,
      lineHeight: 1,
      padding: 0,
      transition: "background-color 0.2s ease-out, color 0.2s ease-out",
      "&:hover": {
        backgroundColor: "#E6E6E6",
        color: theme.palette.black,
      },
      "&:focus": {
        outline: `2px solid ${theme.palette.colors.border.light}`,
        outlineOffset: 1,
      },
    },
  }));

type Props = {
  width?: number | string;
  maxWidth?: number | string;
  onChange?: any;
  value?: string;
  placeholder?: string;
  id?: string;
  onClear?: () => void;
};

export function SearchField({
  width,
  maxWidth,
  onChange,
  value,
  placeholder,
  id,
  onClear,
}: Props) {
  const styleProps = { width, maxWidth };
  const classes: any = useStyles(styleProps)({});
  const { t } = useTranslation("common");
  const showClearButton = Boolean(value);

  return (
    <TextField
      id={id || "offer-search"}
      type="search"
      placeholder={placeholder ?? t("search")}
      value={value}
      onChange={onChange}
      className={classes.searchBar}
      InputProps={{
        classes: { root: classes.root, input: classes.input },
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon width={16} style={{ marginLeft: 3, marginRight: 5 }} />
          </InputAdornment>
        ),
        endAdornment: showClearButton ? (
          <InputAdornment position="end">
            <button
              type="button"
              aria-label={t("clear")}
              className={classes.clearButton}
              onMouseDown={(event) => event.preventDefault()}
              onClick={onClear}
            >
              <span aria-hidden>&times;</span>
            </button>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}
